import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import SalesOutletData from "./SalesOutletData";
import SalesChartData from "./SalesChartData";
import Pagination from "../Pagination";
import {
  Megaphone,
  Package,
  Store,
  TrendingUp,
  FileDown,
  RefreshCcw,
  Mail,
  User,
  Users,
  Send,
  IndianRupee,
  ShieldCheck,
  X,
} from "lucide-react";
import LoadingScreen from "../Utils/LoadingScreen";
import "../Sales Person/CreateSalesPerson.css";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
} from "recharts";

const RestaurantReports = () => {
  const { api, Products = [], adminId, id } = useContext(GlobalContext);

  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const { currentAdmin } = useContext(GlobalContext);
  const effectiveAdminId = adminId || sessionAdminId;
  const [loadingPage, setLoadingPage] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedOutletPie, setSelectedOutletPie] = useState("");
  const [cardsData, setCardsData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingPie, setLoadingPie] = useState(false);
  const [showViewMore, setShowViewMore] = useState(false);
  const [viewMoreCurrentPage, setViewMoreCurrentPage] = useState(1);

  const lastCallRef = useRef({});

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [cardsFromDate, setCardsFromDate] = useState(getTodayDate());
  const [cardsToDate, setCardsToDate] = useState(getTodayDate());
  const [tableFromDate, setTableFromDate] = useState(getTodayDate());
  const [tableToDate, setTableToDate] = useState(getTodayDate());
  const [pieFromDate, setPieFromDate] = useState(getTodayDate());
  const [pieToDate, setPieToDate] = useState(getTodayDate());

  function useTypewriter(messages, speed = 24, hold = 1200) {
    const [i, setI] = useState(0);
    const [txt, setTxt] = useState("");
    const [phase, setPhase] = useState("typing");

    useEffect(() => {
      let t;
      if (phase === "typing") {
        if (txt.length < messages[i].length)
          t = setTimeout(
            () => setTxt(messages[i].slice(0, txt.length + 1)),
            speed
          );
        else setPhase("holding");
      } else if (phase === "holding") {
        t = setTimeout(() => setPhase("deleting"), hold);
      } else if (phase === "deleting") {
        if (txt.length > 0)
          t = setTimeout(() => setTxt(txt.slice(0, -1)), speed / 1.6);
        else {
          setI((i + 1) % messages.length);
          setPhase("typing");
        }
      }
      return () => clearTimeout(t);
    }, [messages, i, txt, phase, speed, hold]);
    return txt;
  }

  const tips = useRef([
    "Monitor top-performing salespersons and identify training needs.",
    "Track daily and monthly sales trends to optimize performance.",
    "Keep outlet information updated for accurate territory mapping.",
    "Regularly review product stock levels to avoid shortages.",
    "Use dashboard filters to analyze region-wise or product-wise sales.",
    "Compare sales growth versus previous months for better forecasting.",
    "Follow up with underperforming outlets to boost conversions.",
    "Review high-selling products and plan promotions accordingly.",
    "Ensure each salesperson updates deal status in real time.",
  ]);

  const typed = useTypewriter(tips.current, 24, 1200);

  useEffect(() => {
    if (!selectedProduct && Products.length > 0) {
      setSelectedProduct(Products[0]?.pk_id ?? Products[0]?.id ?? "");
    }
  }, [Products, selectedProduct]);

  const GetBusinessAnalyticsData = async (
    adminIdParam,
    productId,
    fromDate,
    toDate,
    dataType
  ) => {
    if (!adminIdParam || !productId) return;

    const callKey = `${adminIdParam}-${productId}-${fromDate}-${toDate}-${dataType}`;

    if (lastCallRef.current[dataType] === callKey) {
      return;
    }

    lastCallRef.current[dataType] = callKey;

    const isAllType = dataType === "all";

    if (isAllType) {
      setLoadingCards(true);
      setLoadingTable(true);
      setLoadingPie(true);
      if (!cardsData.length) setLoadingPage(true);
    } else {
      if (dataType === "cards") {
        setLoadingCards(true);
        if (!cardsData.length) setLoadingPage(true);
      } else if (dataType === "table") {
        setLoadingTable(true);
      } else if (dataType === "pie") {
        setLoadingPie(true);
      }
    }

    try {
      const payload = {
        adminId: String(adminIdParam),
        // userId: String(id),
        userId: "",
        productId: String(productId),
        fromDate: fromDate || "",
        toDate: toDate || "",
      };

      const res = await api.post("Admin/v1.0/getBusinessAnalytics", payload);

      let transformedData = [];
      if (res?.data?.responseCode === "00" && Array.isArray(res.data.data)) {
        res.data.data.forEach((outlet) => {
          outlet.data1?.forEach((item) => {
            transformedData.push({
              outletId: outlet.pk_outlet_id,
              name: outlet.outletName,
              label: item.dataLabel,
              value: item.totalValue,
              count: item.totalCount,
            });
          });
        });
      }

      if (isAllType) {
        setCardsData(transformedData);
        setTableData(transformedData);
        setPieData(transformedData);
        setLoadingPage(false);
      } else {
        if (dataType === "cards") {
          setCardsData(transformedData);
          setLoadingPage(false);
        } else if (dataType === "table") {
          setTableData(transformedData);
        } else if (dataType === "pie") {
          setPieData(transformedData);
        }
      }
    } catch (err) {
      console.error(`${dataType} API failed:`, err);
      if (isAllType || dataType === "cards") {
        setCardsData([]);
        setLoadingPage(false);
      }
      if (isAllType || dataType === "table") {
        setTableData([]);
      }
      if (isAllType || dataType === "pie") {
        setPieData([]);
      }
    } finally {
      if (isAllType) {
        setLoadingCards(false);
        setLoadingTable(false);
        setLoadingPie(false);
      } else {
        if (dataType === "cards") setLoadingCards(false);
        else if (dataType === "table") setLoadingTable(false);
        else if (dataType === "pie") setLoadingPie(false);
      }
    }
  };

  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (!effectiveAdminId || !selectedProduct) return;

    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      GetBusinessAnalyticsData(
        effectiveAdminId,
        selectedProduct,
        cardsFromDate,
        cardsToDate,
        "all"
      );
    }
  }, [effectiveAdminId, selectedProduct]);

  useEffect(() => {
    if (!effectiveAdminId || !selectedProduct || !initialLoadRef.current)
      return;

    GetBusinessAnalyticsData(
      effectiveAdminId,
      selectedProduct,
      cardsFromDate,
      cardsToDate,
      "cards"
    );
  }, [cardsFromDate, cardsToDate]);

  useEffect(() => {
    if (!effectiveAdminId || !selectedProduct || !initialLoadRef.current)
      return;

    GetBusinessAnalyticsData(
      effectiveAdminId,
      selectedProduct,
      tableFromDate,
      tableToDate,
      "table"
    );
  }, [tableFromDate, tableToDate]);

  useEffect(() => {
    if (!effectiveAdminId || !selectedProduct || !initialLoadRef.current)
      return;

    GetBusinessAnalyticsData(
      effectiveAdminId,
      selectedProduct,
      pieFromDate,
      pieToDate,
      "pie"
    );
  }, [pieFromDate, pieToDate]);

  const ShimmerCard = () => (
    <div className="h-8 bg-gray-200 rounded animate-pulse w-full mt-1"></div>
  );

  const cardColors = [
    {
      hover: "hover:bg-green-100",
      circle: "bg-green-200",
      circleBorder: "border-green-500",
      gradientFrom: "#22c55e",
      gradientTo: "#047857",
    },
    {
      hover: "hover:bg-blue-100",
      circle: "bg-blue-200",
      circleBorder: "border-blue-500",
      gradientFrom: "#3b82f6",
      gradientTo: "#1e40af",
    },
    {
      hover: "hover:bg-purple-100",
      circle: "bg-purple-200",
      circleBorder: "border-purple-500",
      gradientFrom: "#a78bfa",
      gradientTo: "#6b21a8",
    },
  ];

  const getStatusBadge = (status) => {
    const baseClasses =
      "px-2 py-1 w-32 text-center rounded inline-block text-xs font-medium dark:text-gray-100";

    if (status === "campaign")
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100`;
    if (status === "order")
      return `${baseClasses} bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-100`;
    if (status === "payment")
      return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100`;
    if (status === "pos")
      return `${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100`;
    if (status === "messages")
      return `${baseClasses} bg-sky-100 text-sky-800 dark:bg-sky-800 dark:text-sky-100`;
    if (status === "subscription")
      return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100`;
    if (status === "leads")
      return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100`;

    return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100`;
  };

  const capitalizeText = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const outletOptions = Array.from(
    new Set([
      ...cardsData.map((item) => item.name),
      ...tableData.map((item) => item.name),
      ...pieData.map((item) => item.name),
    ])
  );

  const filteredTableData = selectedOutlet
    ? tableData.filter((item) => item.name === selectedOutlet)
    : tableData;

  const groupedByOutlet = Object.values(
    cardsData.reduce((grouped, item) => {
      if (!grouped[item.name])
        grouped[item.name] = { name: item.name, data: {} };
      grouped[item.name].data[item.label?.toLowerCase()] = {
        value: item.value,
        count: item.count,
      };
      return grouped;
    }, {})
  );

  const aggregatedPieData = useMemo(() => {
    const dataToAggregate = selectedOutletPie
      ? pieData.filter((item) => item.name === selectedOutletPie)
      : pieData;

    const aggregated = dataToAggregate.reduce((acc, item) => {
      const existing = acc.find((a) => a.label === item.label);
      const numValue = parseFloat(item.value) || 0;
      if (existing) {
        existing.value += numValue;
      } else {
        acc.push({ label: item.label, value: numValue });
      }
      return acc;
    }, []);

    return aggregated
      .filter((item) => item.value > 0)
      .map((item) => ({
        ...item,
        value:
          item.value % 1 === 0 ? item.value : parseFloat(item.value.toFixed(2)),
      }));
  }, [pieData, selectedOutletPie]);

  const pieTotal = useMemo(
    () => aggregatedPieData.reduce((sum, item) => sum + item.value, 0),
    [aggregatedPieData]
  );

  const itemsPerPage = 8;
  const paginatedData = filteredTableData.slice(0, itemsPerPage);

  const viewMoreItemsPerPage = 10;
  const viewMorePaginatedData = useMemo(() => {
    const start = (viewMoreCurrentPage - 1) * viewMoreItemsPerPage;
    const end = start + viewMoreItemsPerPage;
    return filteredTableData.slice(start, end);
  }, [filteredTableData, viewMoreCurrentPage]);

  if (loadingPage) {
    return (
      <div className="relative min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center rounded-2xl shadow">
        <LoadingScreen />
      </div>
    );
  }

  if (showViewMore) {
    return (
      <div className="aoc-csp-screen w-full min-h-[90svh] bg-gradient-to-b from-gray-50 to-white shadow rounded-2xl">
        <div className="aoc-csp-container w-full px-4 py-6 md:py-4">
          <div className="flex items-center justify-between mb-6 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 dark:bg-blue-900">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                Business Analytics Data
              </h1>
            </div>

            <button
              onClick={() => {
                setShowViewMore(false);
                setViewMoreCurrentPage(1);
              }}
              className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition"
            >
              <X size={20} />
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-3 dark:bg-gray-800 transition-colors">
            <div className="flex space-between justify-end gap-4 mb-2">
              <div className="flex gap-4">
                <input
                  type="date"
                  value={tableFromDate}
                  onChange={(e) => setTableFromDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                />
                <input
                  type="date"
                  value={tableToDate}
                  onChange={(e) => setTableToDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                />
                <div className="aoc-csp-control-new">
                  <select
                    className="rounded px-3 py-1 text-sm aoc-csp-input"
                    value={selectedOutlet}
                    onChange={(e) => {
                      setSelectedOutlet(e.target.value);
                      setViewMoreCurrentPage(1);
                    }}
                  >
                    <option value="">All Outlets</option>
                    {outletOptions.map((name, idx) => (
                      <option key={idx} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="aoc-csp-table w-full">
                <thead className="sticky top-0 z-20">
                  <tr>
                    <th>Outlet Name</th>
                    <th>Data Label</th>
                    <th>Total Value</th>
                    <th>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTable ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-4 text-gray-500"
                      >
                        <LoadingScreen />
                      </td>
                    </tr>
                  ) : viewMorePaginatedData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-4 text-gray-500"
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    viewMorePaginatedData.map((deal, idx) => (
                      <tr key={idx}>
                        <td>{deal.name}</td>
                        <td>
                          <span className={getStatusBadge(deal.label)}>
                            {deal.label?.charAt(0).toUpperCase() +
                              deal.label?.slice(1)}
                          </span>
                        </td>

                        <td>
                          {Number(deal.value) % 1 === 0
                            ? deal.value
                            : parseFloat(deal.value).toFixed(2)}
                        </td>
                        <td>{deal.count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredTableData.length > viewMoreItemsPerPage && (
              <div className="mt-5 pb-2 flex justify-center">
                <Pagination
                  totalItems={filteredTableData.length}
                  itemsPerPage={viewMoreItemsPerPage}
                  currentPage={viewMoreCurrentPage}
                  onPageChange={(page) => setViewMoreCurrentPage(page)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aoc-csp-screen w-full min-h-[90svh] bg-gradient-to-b from-gray-50 to-white shadow rounded-2xl">
      <div className="aoc-csp-container w-full px-4 py-6 md:py-4">
        <div className="aoc-csp-header flex items-center justify-between mb-4 md:mb-5 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 dark:bg-blue-900">
              <TrendingUp className="h-6 w-6 text-blue-500" />
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
              Restaurant Reports
            </h1>
          </div>
          <div className="flex gap-4 ml-4 justify-end">
            <button
              className="btn btn-refresh"
              onClick={() => {
                lastCallRef.current.cards = null;
                GetBusinessAnalyticsData(
                  effectiveAdminId,
                  selectedProduct,
                  cardsFromDate,
                  cardsToDate,
                  "cards"
                );
              }}
            >
              <RefreshCcw size={14} />
              <span>Refresh</span>
            </button>
            <button
              className="btn btn-export"
              onClick={() =>
                exportAnyJsonToExcel({
                  data: cardsData,
                  filePrefix: "Restaurant Report",
                  sheetName: "cardsList",
                })
              }
            >
              <FileDown size={14} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* welcome Section */}
        <div className="mb-4 rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-blue-100 text-blue-500">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h1 className="text-xl font-semibold">
                  Welcome, {currentAdmin?.name || "Admin"}
                </h1>
                <div className="text-xs text-gray-600 h-5">
                  <span className="font-medium">{typed}</span>
                  <span className="ml-0.5 inline-block w-2 animate-pulse rounded bg-gray-700 align-middle">
                    &nbsp;
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards Section */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm mb-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h5 className="text-xl md:text-xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight flex items-center gap-3">
              Business Data
            </h5>

            <div className="flex items-center gap-3">
              <input
                type="date"
                value={cardsFromDate}
                onChange={(e) => setCardsFromDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm border rounded-lg px-3 py-2"
              />
              <input
                type="date"
                value={cardsToDate}
                onChange={(e) => setCardsToDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 overflow-y-auto"
            style={{ maxHeight: "400px" }}
          >
            {groupedByOutlet.map((outlet, index) => {
              const color = cardColors[index % cardColors.length];
              const dataTypes = Object.entries(outlet.data);

              return (
                <div
                  key={index}
                  className={`relative group bg-white p-5 rounded-xl shadow hover:shadow-sm transition overflow-hidden ${color.hover}`}
                >
                  <svg
                    className="absolute top-0 left-0 w-20 h-20"
                    viewBox="0 0 50 50"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id={`grad-${index}`}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor={color.gradientFrom} />
                        <stop offset="100%" stopColor={color.gradientTo} />
                      </linearGradient>
                    </defs>
                    <path
                      d="M6.79256 35.583C7.46215 33.084 7.1232 30.5541 6.03881 28.4126L6.05122 28.4159C1.71867 19.8314 1.82327 12.5953 14.3459 6.27737L14.3211 6.27073C17.9395 4.44894 20.8071 1.16321 21.9363 -3.0635C24.0392 -10.9239 19.3773 -18.9986 11.5185 -21.1076C3.66207 -23.2128 -4.41652 -18.5486 -6.52559 -10.6898C-7.65708 -6.46707 -6.82275 -2.18942 -4.59771 1.20145L-4.62252 1.1948C3.07192 12.9332 -0.451626 19.2499 -8.49992 24.5203L-8.48752 24.5236C-10.4974 25.836 -12.0559 27.8575 -12.7255 30.3565C-14.1693 35.745 -10.9713 41.284 -5.58281 42.7279C-0.190333 44.1695 5.34871 40.9715 6.79256 35.583Z"
                      fill={`url(#grad-${index})`}
                    />
                  </svg>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    {outlet.name || "Unknown Outlet"}
                  </h2>

                  {loadingCards ? (
                    <ShimmerCard />
                  ) : (
                    <div className="flex justify-between items-start gap-4">
                      <div
                        className="flex gap-4 overflow-x-auto"
                        style={{
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        {dataTypes.map(([label, data]) => {
                          const labelLower = label.toLocaleLowerCase();
                          const colorConfig = {
                            campaign: {
                              bg: "bg-green-50",
                              text: "text-green-700",
                              icon: Megaphone,
                            },
                            order: {
                              bg: "bg-pink-50",
                              text: "text-pink-700",
                              icon: Store,
                            },
                            subscription: {
                              bg: "bg-orange-50",
                              text: "text-orange-700",
                              icon: Mail,
                            },
                            payment: {
                              bg: "bg-blue-50",
                              text: "text-blue-700",
                              icon: IndianRupee,
                            },
                            pos: {
                              bg: "bg-purple-50",
                              text: "text-purple-700",
                              icon: Package,
                            },
                            messages: {
                              bg: "bg-sky-50",
                              text: "text-sky-700",
                              icon: Send,
                            },
                            leads: {
                              bg: "bg-yellow-50",
                              text: "text-yellow-700",
                              icon: Users,
                            },
                          };

                          const config = colorConfig[labelLower] || {
                            bg: "bg-gray-50",
                            text: "text-gray-700",
                            icon: User,
                          };

                          const IconComponent = config.icon;
                          return (
                            <div
                              key={label}
                              className={`flex-1 ${config.bg} p-3 rounded-md min-w-[170px]`}
                            >
                              <h3
                                className={`text-md font-semibold ${config.text} flex items-center gap-2 mb-1 capitalize`}
                              >
                                <IconComponent size={16} /> {label}
                              </h3>
                              <p className="text-gray-700 text-sm">
                                Total value:{" "}
                                {Number(data.value) % 1 === 0
                                  ? data.value
                                  : parseFloat(data.value).toFixed(2)}
                              </p>
                              <p className="text-gray-700 text-sm">
                                Total Count: {data.count}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Table Section */}
          <div className="bg-white rounded-lg shadow p-4 dark:bg-gray-800 transition-colors">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Business Analytics Data
              </h2>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mb-4">
              <input
                type="date"
                value={tableFromDate}
                onChange={(e) => setTableFromDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
              />
              <input
                type="date"
                value={tableToDate}
                onChange={(e) => setTableToDate(e.target.value)}
                className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
              />
              <div>
                <select
                  className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-gray-100 px-3 py-1 text-sm"
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                >
                  <option value="">All Outlets</option>
                  {outletOptions.map((name, idx) => (
                    <option key={idx} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="max-h-80 overflow-y-auto relative">
                <table className="aoc-csp-table w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2">Outlet Name</th>
                      <th className="text-left px-3 py-2">Data Label</th>
                      <th className="text-left px-3 py-2">Total Value</th>
                      <th className="text-left px-3 py-2">Total Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingTable ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center py-4 text-gray-500"
                        >
                          <LoadingScreen />
                        </td>
                      </tr>
                    ) : paginatedData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center py-4 text-gray-500"
                        >
                          No deals found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((deal, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-3 py-2">{deal.name}</td>
                          <td className="px-3 py-2">
                            <span className={getStatusBadge(deal.label)}>
                              {capitalizeText(deal.label)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {Number(deal.value) % 1 === 0
                              ? deal.value
                              : parseFloat(deal.value).toFixed(2)}
                          </td>
                          <td className="px-3 py-2">{deal.count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* View More */}
            {filteredTableData.length > itemsPerPage && (
              <div className="mt-5 flex justify-center">
                <button
                  onClick={() => setShowViewMore(true)}
                  className="px-6 py-2 text-blue-500 font-medium rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition"
                >
                  View All
                </button>
              </div>
            )}
          </div>

          {/* Pie Chart Section */}
          <div className="bg-white rounded-lg shadow p-6 dark:bg-gray-800 transition-colors">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Business Pie Chart Data
              </h2>
            </div>
            <div className="flex space-between justify-end gap-4">
              <div className="flex gap-4">
                <input
                  type="date"
                  value={pieFromDate}
                  onChange={(e) => setPieFromDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                />
                <input
                  type="date"
                  value={pieToDate}
                  onChange={(e) => setPieToDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                />
                <div className="aoc-csp-control-new">
                  <select
                    className="rounded px-3 py-1 text-sm aoc-csp-input"
                    value={selectedOutletPie}
                    onChange={(e) => setSelectedOutletPie(e.target.value)}
                  >
                    <option value="">All Outlets</option>
                    {outletOptions.map((name, idx) => (
                      <option key={idx} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loadingPie ? (
              <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : aggregatedPieData.length === 0 ? (
              <div className="h-96 flex items-center justify-center text-gray-500">
                No data available
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={aggregatedPieData}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      label={false}
                      labelLine={false}
                      minAngle={1}
                      isAnimationActive={false}
                    >
                      {aggregatedPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            [
                              "#22c55e",
                              "#3b82f6",
                              "#a78bfa",
                              "#f59e0b",
                              "#ef4444",
                              "#10b981",
                              "#ec4899",
                            ][index % 7]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => {
                        const percentage = pieTotal
                          ? ((value / pieTotal) * 100).toFixed(1)
                          : 0;

                        return [
                          `${value} (${percentage}%)`,
                          name.charAt(0).toUpperCase() + name.slice(1),
                        ];
                      }}
                    />
                    <Legend
                      formatter={(value) => (
                        <span className="capitalize">{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Sales Chart Data */}
          <SalesChartData
            selectedProductProp={selectedProduct}
            chartTypeProp="Business"
          />

          {/* Sales Outlet Data */}
          <SalesOutletData
            selectedProductProp={selectedProduct}
            selectedOutletChartProp=""
            outletChartTypeProp="Order"
          />
        </div>
      </div>
    </div>
  );
};

export default RestaurantReports;
